import React, { useState, useEffect } from 'react';
import { 
    Pane, 
    Text, 
    IconButton, 
    Badge, 
    Card,
    Button,
    ChevronDownIcon,
    ChevronRightIcon,
    ChevronLeftIcon,
    EditIcon,
    EyeOpenIcon,
    TrashIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    SavedIcon
} from 'evergreen-ui';
import { Link } from 'react-router-dom';

const CONTENT_URL = process.env.REACT_APP_CONTENT_URL || 'https://content.krit.ro/';

const getIconUrl = (icon) => {
    if (!icon) return null;
    if (icon.startsWith('http')) return icon;
    return `${CONTENT_URL}public-assets/categories/${icon}`;
};

// Individual category item
const CategoryItem = ({ 
    category, 
    depth = 0, 
    expandedIds,
    toggleExpanded,
    onDelete,
    onMoveUp,
    onMoveDown,
    onIndent,
    onOutdent,
    isFirst,
    isLast,
    hasParent,
    renderChildren
}) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedIds.includes(category.id);
    const iconUrl = getIconUrl(category.icon);

    return (
        <div>
            <Card
                background="white"
                padding={8}
                marginBottom={4}
                marginLeft={depth * 32}
                border="default"
                display="flex"
                alignItems="center"
                gap={4}
            >
                {/* Move Up/Down buttons */}
                <Pane display="flex" flexDirection="column" marginRight={4}>
                    <IconButton
                        icon={ArrowUpIcon}
                        appearance="minimal"
                        size="small"
                        disabled={isFirst}
                        onClick={onMoveUp}
                        title="Muta sus"
                    />
                    <IconButton
                        icon={ArrowDownIcon}
                        appearance="minimal"
                        size="small"
                        disabled={isLast}
                        onClick={onMoveDown}
                        title="Muta jos"
                    />
                </Pane>

                {/* Indent/Outdent buttons */}
                <Pane display="flex" flexDirection="column" marginRight={4}>
                    <IconButton
                        icon={ChevronLeftIcon}
                        appearance="minimal"
                        size="small"
                        disabled={!hasParent}
                        onClick={onOutdent}
                        title="Muta la nivel superior"
                    />
                    <IconButton
                        icon={ChevronRightIcon}
                        appearance="minimal"
                        size="small"
                        disabled={isFirst}
                        onClick={onIndent}
                        title="Fa subcategorie"
                    />
                </Pane>

                {/* Expand/Collapse */}
                {hasChildren ? (
                    <IconButton
                        icon={isExpanded ? ChevronDownIcon : ChevronRightIcon}
                        appearance="minimal"
                        size="small"
                        onClick={() => toggleExpanded(category.id)}
                    />
                ) : (
                    <Pane width={28} />
                )}

                {/* Icon */}
                {iconUrl && (
                    <img 
                        src={iconUrl} 
                        alt="" 
                        style={{ width: 20, height: 20, objectFit: 'contain' }} 
                    />
                )}

                {/* Title */}
                <Pane flex={1} display="flex" alignItems="center" gap={8}>
                    <Text fontWeight={500}>{category.title}</Text>
                    {!category.active && <Badge color="red">Inactiv</Badge>}
                    <Text size={300} color="muted">/{category.slug}</Text>
                </Pane>

                <Text size={300} color="muted">#{category.id}</Text>

                {/* Action buttons */}
                <Link to={`/categories/${category.id}`}>
                    <IconButton icon={EyeOpenIcon} appearance="minimal" size="small" title="Detalii" />
                </Link>
                <Link to={`/categories/${category.id}/edit`}>
                    <IconButton icon={EditIcon} appearance="minimal" size="small" title="Editeaza" />
                </Link>
                <IconButton 
                    icon={TrashIcon} 
                    appearance="minimal" 
                    size="small" 
                    intent="danger"
                    onClick={() => onDelete(category.id)}
                    title="Sterge"
                />
            </Card>

            {hasChildren && isExpanded && renderChildren(category.children, depth + 1, category.id)}
        </div>
    );
};

const CategoryTreeManager = ({ 
    categories, 
    onRestructure, 
    onDelete,
    loading 
}) => {
    const [expandedIds, setExpandedIds] = useState([]);
    const [localTree, setLocalTree] = useState([]);
    const [hasChanges, setHasChanges] = useState(false);

    // Sync local state with props
    useEffect(() => {
        if (categories) {
            setLocalTree(JSON.parse(JSON.stringify(categories)));
            setHasChanges(false);
        }
    }, [categories]);

    // Expand all by default on mount
    useEffect(() => {
        if (categories && categories.length > 0) {
            const getAllIds = (cats) => {
                let ids = [];
                cats.forEach(cat => {
                    ids.push(cat.id);
                    if (cat.children && cat.children.length > 0) {
                        ids = ids.concat(getAllIds(cat.children));
                    }
                });
                return ids;
            };
            setExpandedIds(getAllIds(categories));
        }
    }, [categories]);

    const toggleExpanded = (id) => {
        setExpandedIds(prev => 
            prev.includes(id) 
                ? prev.filter(x => x !== id)
                : [...prev, id]
        );
    };

    // Find parent array and index of a category
    const findInTree = (tree, id, parent = null) => {
        for (let i = 0; i < tree.length; i++) {
            if (tree[i].id === id) {
                return { array: tree, index: i, parent };
            }
            if (tree[i].children && tree[i].children.length > 0) {
                const found = findInTree(tree[i].children, id, tree[i]);
                if (found) return found;
            }
        }
        return null;
    };

    // Move item up within its siblings
    const handleMoveUp = (id) => {
        const newTree = JSON.parse(JSON.stringify(localTree));
        const found = findInTree(newTree, id);
        if (!found || found.index === 0) return;

        const [item] = found.array.splice(found.index, 1);
        found.array.splice(found.index - 1, 0, item);

        setLocalTree(newTree);
        setHasChanges(true);
    };

    // Move item down within its siblings
    const handleMoveDown = (id) => {
        const newTree = JSON.parse(JSON.stringify(localTree));
        const found = findInTree(newTree, id);
        if (!found || found.index >= found.array.length - 1) return;

        const [item] = found.array.splice(found.index, 1);
        found.array.splice(found.index + 1, 0, item);

        setLocalTree(newTree);
        setHasChanges(true);
    };

    // Make item a child of previous sibling (indent)
    const handleIndent = (id) => {
        const newTree = JSON.parse(JSON.stringify(localTree));
        const found = findInTree(newTree, id);
        if (!found || found.index === 0) return;

        const [item] = found.array.splice(found.index, 1);
        const prevSibling = found.array[found.index - 1];
        
        if (!prevSibling.children) {
            prevSibling.children = [];
        }
        prevSibling.children.push(item);

        // Expand parent
        if (!expandedIds.includes(prevSibling.id)) {
            setExpandedIds(prev => [...prev, prevSibling.id]);
        }

        setLocalTree(newTree);
        setHasChanges(true);
    };

    // Move item to parent's level (outdent)
    const handleOutdent = (id) => {
        const newTree = JSON.parse(JSON.stringify(localTree));
        const found = findInTree(newTree, id);
        if (!found || !found.parent) return;

        // Remove from current parent
        const [item] = found.array.splice(found.index, 1);

        // Find grandparent array
        const grandparentFound = findInTree(newTree, found.parent.id);
        if (grandparentFound) {
            // Insert after parent
            grandparentFound.array.splice(grandparentFound.index + 1, 0, item);
        } else {
            // Parent is at root, add to root
            newTree.push(item);
        }

        setLocalTree(newTree);
        setHasChanges(true);
    };

    // Save changes
    const handleSave = () => {
        onRestructure(localTree);
        setHasChanges(false);
    };

    // Discard changes
    const handleDiscard = () => {
        setLocalTree(JSON.parse(JSON.stringify(categories)));
        setHasChanges(false);
    };

    // Recursive render function
    const renderCategories = (cats, depth = 0, parentId = null) => {
        return cats.map((category, index) => (
            <CategoryItem
                key={category.id}
                category={category}
                depth={depth}
                expandedIds={expandedIds}
                toggleExpanded={toggleExpanded}
                onDelete={onDelete}
                onMoveUp={() => handleMoveUp(category.id)}
                onMoveDown={() => handleMoveDown(category.id)}
                onIndent={() => handleIndent(category.id)}
                onOutdent={() => handleOutdent(category.id)}
                isFirst={index === 0}
                isLast={index === cats.length - 1}
                hasParent={parentId !== null}
                renderChildren={renderCategories}
            />
        ));
    };

    if (!localTree || localTree.length === 0) {
        return (
            <Pane padding={32} textAlign="center">
                <Text color="muted">Nu exista categorii. Creeaza prima categorie.</Text>
            </Pane>
        );
    }

    return (
        <Pane>
            {hasChanges && (
                <Pane 
                    marginBottom={16}
                    padding={12}
                    background="tint2"
                    borderRadius={4}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    border="default"
                >
                    <Text>
                        <strong>Modificari nesalvate</strong> - Nu uita sa salvezi!
                    </Text>
                    <Pane display="flex" gap={8}>
                        <Button onClick={handleDiscard}>
                            Anuleaza
                        </Button>
                        <Button appearance="primary" intent="success" iconBefore={SavedIcon} onClick={handleSave}>
                            Salveaza Modificarile
                        </Button>
                    </Pane>
                </Pane>
            )}

            {renderCategories(localTree)}
        </Pane>
    );
};

export default CategoryTreeManager;
